import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../../app/store/utils/baseQueryWithReauth';
import type {
  Category,
  CreateCategoryInput,
  UpdateCategoryInput,
} from '../../application/types';
import { unwrapApiData } from './unwrapApiData';

export const SYSTEM_CATEGORIES_BASE = '/system/owner/categories';

export const categoryApi = createApi({
  reducerPath: 'categoryApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Category'],
  endpoints: build => ({
    getCategories: build.query<Category[], void>({
      query: () => ({
        url: SYSTEM_CATEGORIES_BASE,
        method: 'GET',
      }),
      transformResponse: (response: unknown) => unwrapApiData<Category[]>(response),
      providesTags: ['Category'],
    }),

    getCategoryById: build.query<Category, number>({
      query: id => ({
        url: `${SYSTEM_CATEGORIES_BASE}/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: unknown) => unwrapApiData<Category>(response),
      providesTags: (_result, _error, id) => [{ type: 'Category', id }],
    }),

    createCategory: build.mutation<Category, CreateCategoryInput>({
      query: body => ({
        url: SYSTEM_CATEGORIES_BASE,
        method: 'POST',
        body,
      }),
      transformResponse: (response: unknown) => unwrapApiData<Category>(response),
      invalidatesTags: ['Category'],
    }),

    updateCategory: build.mutation<
      Category,
      { id: number; body: UpdateCategoryInput }
    >({
      query: ({ id, body }) => ({
        url: `${SYSTEM_CATEGORIES_BASE}/${id}`,
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: unknown) => unwrapApiData<Category>(response),
      invalidatesTags: (_result, _error, { id }) => [
        'Category',
        { type: 'Category', id },
      ],
    }),

    deleteCategory: build.mutation<void, number>({
      query: id => ({
        url: `${SYSTEM_CATEGORIES_BASE}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Category'],
    }),
  }),
});

export const {
  useGetCategoriesQuery,
  useGetCategoryByIdQuery,
  useCreateCategoryMutation,
  useUpdateCategoryMutation,
  useDeleteCategoryMutation,
} = categoryApi;
